import React, { useRef, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Vega } from "react-vega";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import className from "../style.module.scss";

import {
  removeSpreadsheetPlot,
  updatePlotVariables,
  updatePlotSize,
} from "../Reducers/actions";

import {
  Collapse,
  Expand,
  Pencil,
} from "@learningblocks/shared-components/lib/Icons";

import { incrementUnsanitisedSheetChanges } from "@learningblocks/simulations/spreadsheet-sim-components/lib/ObjectivesContainer/Reducers/actions.js";

/**
 * PlotWrapper Component
 * Manages the draggable, resizable plot within the spreadsheet application.
 * Allows for editing plot headers, resizing the plot, and dynamic plot rendering using Vega.
 */
const PlotWrapper = (props) => {
  const {
    sceneIndex,
    currentObjectiveIndex,
    currentStepGroupIndex,
    currentStepIndex,
  } = props;

  const parentRef = useRef(null);
  const plotWrapperRef = useRef(null);

  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  });
  const [size, setSize] = useState(props.plotData.size);
  const [isResizing, setIsResizing] = useState(false);
  const [active, setActive] = useState(9999);
  const [editing, setEditing] = useState(false);
  const [minimized, setMinimized] = useState(false);

  const [title, setTitle] = useState(props.plotData.title);
  const [headerX, setHeaderX] = useState(props.plotData.headerX);
  const [headerY, setHeaderY] = useState(props.plotData.headerY);

  const PlotTypes = {
    BASIC_BAR: "BASIC_BAR",
    BOX_MIN_MAX: "BOX_MIN_MAX",
    CONTROL_HISTOGRAM: "CONTROL_HISTOGRAM",
    LINE_BASIC: "LINE_BASIC",
  };

  // Initialize event listeners and cleanup on component unmount
  useEffect(() => {
    updateBounds();

    window.addEventListener("resize", updateBounds);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("resize", updateBounds);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update bounds and plot size when minimized or resized
  useEffect(() => {
    updateBounds();
  }, [size, minimized]);

  useEffect(() => {
    if (!minimized) {
      props.updatePlotSize({ key: props.plotData.id, size: size });
    }
  }, [size]);

  // Handle plot editing state when not active
  useEffect(() => {
    if (!active) {
      setEditing(false);
    }
  }, [active]);

  // Update editing state when changes to the plot data occur
  useEffect(() => {
    if (editing) {
      setTitle(props.plotData.title);
      setHeaderX(props.plotData.headerX);
      setHeaderY(props.plotData.headerY);
    }
    // Fix for vega safari bug preventing proper focus on range inputs
    document
      .querySelectorAll('.vega-bindings input[type="range"]')
      .forEach((el) => {
        el.setAttribute("tabindex", "0");
      });
  }, [editing]);

  // Set the active state for newly created plots
  useEffect(() => {
    if (plotWrapperRef.current) {
      setActive(false); // Places newer plots on top
    }
  }, [props.plotCount]);

  useEffect(() => {
    if (!editing) {
      if (props.plotData.type === "CONTROL_HISTOGRAM") {
        requestAnimationFrame(() => {
          // execute after browser has finished current rendering cycle
          updateHistogramControls();
        });
      }
    }
  }, [
    editing,
    props.plotData.histogramBinWidth,
    props.plotData.histogramRangeMin,
    props.plotData.histogramRangeMax,
  ]);

  /**
   * Update plot bounds to set draggable area limits based on the parent container.
   */
  const updateBounds = () => {
    if (parentRef.current) {
      const parentRect = parentRef.current.getBoundingClientRect();
      setBounds({
        left: 5,
        top: 40,
        right: parentRect.width - 25 - (!minimized ? size.x : 100),
        bottom: parentRect.height - 25 - (!minimized ? size.y : 10),
      });
    }
  };

  /**
   * Handle clicks outside of the current plot wrapper to deactivate it.
   * @param {Event} event - The DOM event object.
   */
  const handleClickOutside = (event) => {
    if (
      plotWrapperRef.current &&
      !plotWrapperRef.current.contains(event.target)
    ) {
      const allElements = document.getElementsByClassName(
        className.PlotWrapper
      );
      for (const element of allElements) {
        if (
          element !== plotWrapperRef.current &&
          element.contains(event.target)
        ) {
          setActive(false);
          break;
        }
      }
    }
  };

  /**
   * Activate the plot when it is clicked to give it z-index priority.
   */
  const handleClickInside = () => {
    setActive(true);
  };

  /**
   * Update histogram controls to reflect the current plot data.
   * Synchronizes the UI sliders with the plot's histogram parameters.
   */
  const updateHistogramControls = () => {
    const plotContainer = document.getElementById(props.plotData.id);

    if (plotContainer) {
      const updateSlider = (selector, value) => {
        const slider = plotContainer.querySelector(selector);
        if (slider) {
          slider.value = value;
          slider.dispatchEvent(new Event("input", { bubbles: true })); // Trigger update to Vega slider
        }
      };

      updateSlider(
        'input[type="range"][name="histogramBinWidth"]',
        props.plotData.histogramBinWidth
      );
      updateSlider(
        'input[type="range"][name="histogramRangeMin"]',
        props.plotData.histogramRangeMin
      );
      updateSlider(
        'input[type="range"][name="histogramRangeMax"]',
        props.plotData.histogramRangeMax
      );
    }
  };

  const calcPlotHeight = (editing, type, headers) => {
    const toolbarHeight = 30;
    const titleHeight =
      (headers.title !== null &&
        headers.title !== undefined &&
        headers.title !== "") ||
      editing
        ? 24
        : 0;
    const xHeaderHeight =
      (headers.x !== null && headers.x !== undefined && headers.x !== "") ||
      editing
        ? 20
        : 0;
    const controlHistogramHeight =
      editing && type === "CONTROL_HISTOGRAM" ? 160 : 0;

    const plotHeight =
      size.y -
      toolbarHeight -
      controlHistogramHeight -
      xHeaderHeight -
      titleHeight;

    return plotHeight;
  };

  const handlePlotEdit = () => {
    if (editing) {
      // Get the plot container by its id
      const plotContainer = document.getElementById(props.plotData.id);

      // Extract values from the sliders
      const binWidthValue = plotContainer?.querySelector(
        'input[type="range"][name="histogramBinWidth"]'
      )?.value;
      const binRangeMinValue = plotContainer?.querySelector(
        'input[type="range"][name="histogramRangeMin"]'
      )?.value;
      const binRangeMaxValue = plotContainer?.querySelector(
        'input[type="range"][name="histogramRangeMax"]'
      )?.value;

      // Update plot variables with the extracted values and additional props
      props.updatePlotVariables({
        key: props.plotData.id,
        title,
        headerX,
        headerY,
        histogramBinWidth: binWidthValue,
        histogramRangeMin: binRangeMinValue,
        histogramRangeMax: binRangeMaxValue,
      });

      props.incrementUnsanitisedSheetChanges({
        sceneId: sceneIndex,
        objectiveIndex: currentObjectiveIndex,
        stepGroupIndex: currentStepGroupIndex,
        stepIndex: currentStepIndex,
      });
    }

    // Toggle the editing state
    setEditing(!editing);
  };

  const getType = () => {
    switch (props.plotData.type) {
      case PlotTypes.BASIC_BAR:
        return "Bar Chart";
      case PlotTypes.BOX_MIN_MAX:
        return "Box Plot";
      case PlotTypes.CONTROL_HISTOGRAM:
        return "Histogram";
      case PlotTypes.LINE_BASIC:
        return "Line Chart";
      default:
        return "Unknown";
    }
  };

  return (
    <div
      ref={parentRef}
      className={`${className.PlotContainer} ${editing ? "" : className.NonEditContainer}`}
    >
      <Draggable
        cancel="input, .vega-lite-range"
        className={className.PlotDraggable}
        bounds={bounds}
        defaultPosition={{
          x: props.plotData.position.x,
          y: props.plotData.position.y,
        }}
        onStart={handleClickInside}
        disabled={isResizing || editing} // Prevent dragging while resizing or editing
      >
        <div
          className={className.PlotFocus}
          ref={plotWrapperRef}
          style={{
            zIndex: active ? "9999" : "999",
            width: !minimized ? `${size.x + 22}px` : "150px",
            height: !minimized ? `${size.y + 22}px` : "30px",
          }}
          onClick={handleClickInside}
          role="button"
          aria-label={"Focus plot."}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleClickInside();
            }
          }}
        >
          <ResizableBox
            className={className.PlotWrapper}
            width={!minimized ? size.x : 128}
            height={!minimized ? size.y : 20}
            minConstraints={[400, 350]}
            maxConstraints={[700, 500]}
            resizeHandles={!minimized ? undefined : []}
            style={{ width: `${size.x}px`, height: `${size.y}px` }}
            onResizeStart={(e) => {
              if (!minimized) {
                setIsResizing(true);
                e.stopPropagation();
              }
            }}
            onResize={(e, data) => {
              if (!minimized) {
                setSize({
                  x: data.size.width,
                  y: data.size.height,
                });
              }
            }}
            onResizeStop={(e, data) => {
              if (!minimized) {
                setIsResizing(false);
                setSize({
                  x: data.size.width,
                  y: data.size.height,
                });
              }
            }}
          >
            <>
              <div className={className.PlotToolbar}>
                {editing ? (
                  <button onClick={() => setEditing(false)}>Cancel</button>
                ) : null}
                {!minimized && !props.plotData.locked && (
                  <button onClick={() => handlePlotEdit()}>
                    {!editing && (
                      <img className={className.Icon} src={Pencil} alt="" />
                    )}
                    {!editing ? "Edit" : "Confirm"}
                  </button>
                )}
                {!editing ? (
                  <button onClick={() => setMinimized(!minimized)}>
                    <img
                      className={className.Icon}
                      src={!minimized ? Collapse : Expand}
                      alt=""
                    />
                    {!minimized ? "Collapse" : "Expand"}
                  </button>
                ) : null}
                {!editing && !minimized && !props.plotData.required ? (
                  <button
                    onClick={() =>
                      props.removeSpreadsheetPlot(props.plotData.id)
                    }
                  >
                    Remove
                  </button>
                ) : null}
              </div>
              <div
                className={className.PlotTitle}
                style={{
                  display: props.plotData.title || editing ? null : "none",
                }}
              >
                {editing ? (
                  <input
                    className={`${className.HeaderInput} ${editing ? className.ActiveInput : ""}`}
                    type={"text"}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={"title"}
                  />
                ) : !minimized ? (
                  props.plotData.title
                ) : null}
              </div>
              <div
                id={props.plotData.id}
                className={className.Plot}
                style={{
                  display: minimized ? "none" : null,
                  marginLeft: headerY || editing ? "15px" : null,
                }}
              >
                <Vega
                  key={`${props.plotData.title}-${props.plotData.headerX}`}
                  spec={{
                    ...JSON.parse(JSON.stringify(props.spec)), // Deep clone to ensure immutability
                    config: {
                      ...props.spec.config,
                      aria: true, // Enable ARIA attributes
                      description: `Graph ${props.index + 1} (${getType()}). A graph generated from the spreadsheet. Refer to the cell range that you used to generate this graph. Sheet: "${props.plotData.sheetName}". Cell range: ${props.plotData.cellsUsed.range}`, // This sets aria-label
                    },
                  }}
                  actions={false} // Disable Vega's built-in action menu
                  width={size.x - (props.plotData.headerY || editing ? 20 : 0)}
                  height={calcPlotHeight(editing, props.plotData.type, {
                    x: props.plotData.headerX,
                    title: props.plotData.title,
                  })}
                />
              </div>
              <div
                className={className.PlotHeaderX}
                style={{
                  display: props.plotData.headerX || editing ? null : "none",
                }}
              >
                {editing ? (
                  <input
                    className={`${className.HeaderInput} ${editing ? className.ActiveInput : ""}`}
                    type={"text"}
                    value={headerX}
                    onChange={(e) => setHeaderX(e.target.value)}
                    placeholder={"x"}
                  />
                ) : !minimized ? (
                  props.plotData.headerX
                ) : null}
              </div>
              <div
                className={className.PlotHeaderY}
                style={{
                  display: props.plotData.headerY || editing ? null : "none",
                }}
              >
                {editing ? (
                  <input
                    className={`${className.HeaderInput} ${editing ? className.ActiveInput : ""}`}
                    type={"text"}
                    value={headerY}
                    onChange={(e) => setHeaderY(e.target.value)}
                    placeholder={"y"}
                  />
                ) : !minimized ? (
                  props.plotData.headerY
                ) : null}
              </div>
            </>
          </ResizableBox>
        </div>
      </Draggable>
    </div>
  );
};

/**
 * Maps the Redux state to component props.
 */
function mapStateToProps(state, ownProps) {
  const sceneIndex = 8;
  const stepState = state.stepData || {};
  const protocol =
    stepState.scenes?.find((scene) => scene.id === sceneIndex) ||
    ownProps.stepData;
  const currentObjectiveIndex = protocol?.currentObjectiveIndex || 0;
  const currentStepGroupIndex = protocol?.currentStepGroupIndex || 0;
  const currentStepIndex = protocol?.currentStepIndex || 0;

  return {
    ...ownProps,
    sceneIndex: sceneIndex,
    currentObjectiveIndex: currentObjectiveIndex,
    currentStepGroupIndex: currentStepGroupIndex,
    currentStepIndex: currentStepIndex,
  };
}

/**
 * Maps Redux dispatch actions to component props.
 */
function mapDispatchToProps(dispatch) {
  return {
    removeSpreadsheetPlot: (data) => dispatch(removeSpreadsheetPlot(data)),
    updatePlotVariables: (data) => dispatch(updatePlotVariables(data)),
    updatePlotSize: (data) => dispatch(updatePlotSize(data)),
    incrementUnsanitisedSheetChanges: (data) =>
      dispatch(incrementUnsanitisedSheetChanges(data)),
  };
}

// Connect component to Redux store
const mapped_PlotWrapper = connect(
  mapStateToProps,
  mapDispatchToProps
)(PlotWrapper);

export { mapped_PlotWrapper as PlotWrapper };
